import { useState } from "react";
import { AlertTriangle, ArrowUpRight, Check, Quote } from "lucide-react";
import type { InsightBlock } from "@/data/insights/types";

/**
 * Renderizador de bloques editoriales. HTML semántico (todo el texto visible
 * sin JS), con superficies editoriales: comparativa en dos tarjetas, rutas de
 * decisión, tarjetas de criterio y de alerta.
 */
export const InsightBody = ({ blocks }: { blocks: InsightBlock[] }) => (
  <>
    {blocks.map((b, i) => {
      switch (b.type) {
        case "h2":
          return (
            <h2
              key={i}
              id={slugify(b.text ?? "")}
              className="v3-ed-h2 mt-20 scroll-mt-28 text-[color:var(--v3-ink)]"
            >
              {b.text}
            </h2>
          );
        case "h3":
          return (
            <h3 key={i} className="mt-8 text-lg font-semibold tracking-tight text-[color:var(--v3-ink)]">
              {b.text}
            </h3>
          );
        case "p":
          return (
            <p key={i} className="mt-5 text-[1.0625rem] leading-[1.7] text-[color:var(--v3-ink-soft)]">
              {b.text}
            </p>
          );
        case "list": {
          const alert = b.tone === "alert";
          if (!b.tone) {
            return (
              <ul key={i} className="mt-5 list-none space-y-2">
                {(b.items ?? []).map((it, j) => (
                  <li key={j} className="flex gap-3 text-base leading-relaxed text-[color:var(--v3-ink-soft)]">
                    <span aria-hidden="true" className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-[color:var(--v3-muted)]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            );
          }
          const Icon = alert ? AlertTriangle : Check;
          return (
            <ul key={i} className="mt-8 grid list-none gap-3 sm:grid-cols-2">
              {(b.items ?? []).map((it, j) => (
                <li key={j} className={`v3-ed-card flex flex-col gap-4 p-6 ${alert ? "v3-ed-card-alert" : ""}`}>
                  <span
                    aria-hidden="true"
                    className={`flex h-8 w-8 items-center justify-center rounded-full border ${
                      alert
                        ? "border-[color:var(--v3-copper)]/40 text-[color:var(--v3-copper)]"
                        : "border-[color:var(--v3-line-strong)] text-[color:var(--v3-ink)]"
                    }`}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                  </span>
                  <span className="text-[0.98rem] leading-relaxed text-[color:var(--v3-ink-soft)]">{it}</span>
                </li>
              ))}
            </ul>
          );
        }
        case "steps":
          return (
            <ol key={i} className="mt-8 grid list-none gap-3 sm:grid-cols-2">
              {(b.items ?? []).map((it, j) => (
                <li key={j} className="v3-ed-card p-6">
                  <span className="v3-ed-num" aria-hidden="true">{String(j + 1).padStart(2, "0")}</span>
                  <p className="mt-6 text-[1.02rem] font-medium leading-snug tracking-tight text-[color:var(--v3-ink)]">{it}</p>
                </li>
              ))}
            </ol>
          );
        case "table":
          return <CompareBento key={i} head={b.head ?? []} rows={b.rows ?? []} />;
        case "decision":
          return <DecisionRoutes key={i} options={b.options ?? []} />;
        case "callout":
          return (
            <figure key={i} className="v3-ed-card relative mt-12 overflow-hidden p-8 sm:p-10">
              <Quote aria-hidden="true" className="h-7 w-7 text-[color:var(--v3-copper)]" strokeWidth={1.5} />
              <blockquote className="mt-5 text-[1.3rem] font-medium leading-[1.4] tracking-[-0.02em] text-[color:var(--v3-ink)] sm:text-[1.5rem]">
                {b.text}
              </blockquote>
              {b.source && (
                <figcaption className="mt-6">
                  <a
                    href={b.source.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="v3-ed-label inline-flex items-center gap-1.5 hover:text-[color:var(--v3-ink)]"
                  >
                    {b.source.label}
                    <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                </figcaption>
              )}
            </figure>
          );
        case "quote":
          return (
            <blockquote key={i} className="mt-8 border-l border-[color:var(--v3-line-strong)] pl-6 text-lg leading-relaxed tracking-tight text-[color:var(--v3-ink-soft)]">
              {b.text}
              {b.source && (
                <cite className="mt-3 block not-italic">
                  <a href={b.source.href} target="_blank" rel="noopener noreferrer" className="v3-ed-label hover:text-[color:var(--v3-ink)]">
                    {b.source.label}
                  </a>
                </cite>
              )}
            </blockquote>
          );
        default:
          return null;
      }
    })}
  </>
);

/** Comparativa de 3 columnas (criterio, A, B) como dos tarjetas enfrentadas. */
const CompareBento = ({ head, rows }: { head: string[]; rows: string[][] }) => {
  if (head.length !== 3) {
    return (
      <div className="v3-ed-card mt-8 overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-left text-sm">
          <thead>
            <tr>
              {head.map((h) => (
                <th key={h} className="v3-ed-label border-b border-[color:var(--v3-line)] px-5 py-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, j) => (
              <tr key={j} className="align-top">
                {row.map((cell, k) => (
                  <td key={k} className="border-b border-[color:var(--v3-line)] px-5 py-4 leading-relaxed text-[color:var(--v3-ink-soft)]">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  const sides = [1, 2].map((col) => ({ title: head[col], featured: col === 1, items: rows.map((r) => [r[0], r[col]]) }));
  return (
    <div className="mt-8 grid gap-3 md:grid-cols-2">
      {sides.map((s, idx) => (
        <section key={s.title} className={`v3-ed-card p-7 sm:p-8 ${s.featured ? "v3-ed-card-featured" : ""}`} aria-label={s.title}>
          <div className="flex items-center justify-between gap-4">
            <span className="v3-ed-label">{idx === 0 ? "A" : "B"}</span>
            {s.featured && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[color:var(--v3-blue)]" />}
          </div>
          <h3 className="mt-6 text-[1.6rem] font-bold leading-none tracking-[-0.03em] text-[color:var(--v3-ink)]">{s.title}</h3>
          <dl className="mt-8 divide-y divide-[color:var(--v3-line)]">
            {s.items.map(([k, v]) => (
              <div key={k} className="py-4 first:pt-0 last:pb-0">
                <dt className="v3-ed-label">{k}</dt>
                <dd className="mt-1.5 text-[0.98rem] leading-relaxed text-[color:var(--v3-ink-soft)]">{v}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
};

/** Selector de rutas: todo el contenido está en el HTML; el estado solo resalta. */
const DecisionRoutes = ({ options }: { options: { situation: string; model: string; detail: string }[] }) => {
  const [active, setActive] = useState(0);
  return (
    <div className="mt-8 grid gap-3 md:grid-cols-3">
      {options.map((o, j) => {
        const on = j === active;
        return (
          <button
            key={o.model}
            type="button"
            onClick={() => setActive(j)}
            onMouseEnter={() => setActive(j)}
            aria-pressed={on}
            className={`v3-ed-card group flex flex-col p-6 text-left transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--v3-blue)] ${on ? "v3-ed-card-featured" : ""}`}
          >
            <span className="v3-ed-num">{String(j + 1).padStart(2, "0")}</span>
            <span className="mt-6 text-[0.98rem] leading-snug text-[color:var(--v3-ink-soft)]">«{o.situation}»</span>
            <span className="mt-auto pt-8">
              <span className="block h-px w-full bg-[color:var(--v3-line)]" aria-hidden="true">
                <span className={`block h-px bg-[color:var(--v3-blue)] transition-all duration-500 ${on ? "w-full" : "w-0"}`} />
              </span>
              <span className="mt-5 flex items-center justify-between gap-3 text-lg font-bold tracking-[-0.02em] text-[color:var(--v3-ink)]">
                {o.model}
                <ArrowUpRight className={`h-4 w-4 transition-transform duration-300 ${on ? "translate-x-0.5 -translate-y-0.5 text-[color:var(--v3-blue)]" : "text-[color:var(--v3-muted)]"}`} aria-hidden="true" />
              </span>
              <span className="mt-2 block text-sm leading-relaxed text-[color:var(--v3-muted)]">{o.detail}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
